import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Building2, ShieldCheck, Sparkles, Lock, Mail } from 'lucide-react';
import { User } from '../App';
import { toast } from 'sonner';

interface LoginProps {
  onLogin: (user: User) => void;
}

export default function Login({ onLogin }: LoginProps) {
  const [clientEmail, setClientEmail] = useState('');
  const [clientPassword, setClientPassword] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  const handleClientLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (clientEmail && clientPassword) {
      toast.success('Login successful!');
      onLogin({
        id: 'client-001',
        name: 'ABC Trading LLC',
        email: clientEmail,
        role: 'client',
        clientId: 'CLT-001'
      });
    } else {
      toast.error('Please fill in all fields');
    }
  };

  const handleAdminLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (adminEmail && adminPassword) {
      toast.success('Super Admin login successful!');
      onLogin({
        id: 'admin-001',
        name: 'Super Admin',
        email: adminEmail,
        role: 'super-admin'
      });
    } else {
      toast.error('Please fill in all fields');
    }
  };

  const handleDemoClientLogin = () => {
    toast.success('Demo Client login successful!');
    onLogin({
      id: 'client-001',
      name: 'ABC Trading LLC',
      email: 'demo@abctrading.ae',
      role: 'client',
      clientId: 'CLT-001'
    });
  };

  const handleDemoAdminLogin = () => {
    toast.success('Demo Super Admin login successful!');
    onLogin({
      id: 'admin-001',
      name: 'Super Admin',
      email: 'admin@ais-erp.com',
      role: 'super-admin'
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* UAE Building Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1613914011280-fc80bd159816?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxEdWJhaSUyMHNreWxpbmUlMjBidWlsZGluZ3N8ZW58MXx8fHwxNzYyNDI2MDM2fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0052CC]/95 via-indigo-900/90 to-slate-900/95 backdrop-blur-sm"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-2xl mb-4">
            <Building2 className="w-11 h-11 text-[#0052CC]" />
          </div>
          <h1 className="text-4xl text-white mb-2">AIS Connect</h1>
          <p className="text-blue-100">UAE E-Invoicing Portal</p>
        </div>

        {/* Login Form Section */}
        <Card className="premium-shadow-lg border-0 backdrop-blur-md bg-white/95 hover-lift">
          <CardHeader className="space-y-3 pb-6">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#0052CC]" />
              <CardTitle className="text-3xl bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Welcome Back</CardTitle>
            </div>
            <CardDescription className="text-base">Sign in to access UAE E-Invoicing Portal</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="client" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8 p-1 bg-gray-100/80 backdrop-blur-sm">
                <TabsTrigger value="client" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:shadow-md transition-all">
                  <Building2 className="w-4 h-4" />
                  Client Login
                </TabsTrigger>
                <TabsTrigger value="admin" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:shadow-md transition-all">
                  <ShieldCheck className="w-4 h-4" />
                  Super Admin
                </TabsTrigger>
              </TabsList>

              <TabsContent value="client" className="space-y-1">
                <form onSubmit={handleClientLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="client-email" className="text-sm text-gray-700">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="client-email"
                        type="email"
                        placeholder="client@company.com"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        className="pl-10 h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20 transition-all"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client-password" className="text-sm text-gray-700">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="client-password"
                        type="password"
                        placeholder="••••••••"
                        value={clientPassword}
                        onChange={(e) => setClientPassword(e.target.value)}
                        className="pl-10 h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20 transition-all"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <a href="#" className="text-sm text-[#0052CC] hover:text-[#0041A3] transition-colors">
                      Forgot Password?
                    </a>
                  </div>
                  <Button type="submit" className="w-full h-11 bg-gradient-to-r from-[#0052CC] to-indigo-600 hover:from-[#0041A3] hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all">
                    Sign In
                  </Button>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-3 text-gray-500">Or continue with</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 border-2 hover:border-[#0052CC] hover:bg-blue-50 transition-all"
                    onClick={handleDemoClientLogin}
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-[#0052CC]" />
                    Demo Client Login
                  </Button>
                  <div className="text-center pt-4">
                    <a href="#" className="text-sm text-gray-600 hover:text-[#0052CC] transition-colors">
                      Don't have an account? <span className="underline">Register Now</span>
                    </a>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="admin" className="space-y-1">
                <form onSubmit={handleAdminLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="admin-email" className="text-sm text-gray-700">Admin Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="admin-email"
                        type="email"
                        placeholder="admin@ais.com"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        className="pl-10 h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20 transition-all"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-password" className="text-sm text-gray-700">Admin Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="admin-password"
                        type="password"
                        placeholder="••••••••"
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        className="pl-10 h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20 transition-all"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full h-11 bg-gradient-to-r from-[#0052CC] to-indigo-600 hover:from-[#0041A3] hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all">
                    Admin Sign In
                  </Button>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-3 text-gray-500">Or continue with</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 border-2 hover:border-[#0052CC] hover:bg-blue-50 transition-all"
                    onClick={handleDemoAdminLogin}
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-[#0052CC]" />
                    Demo Super Admin Login
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
